import { createReducer } from '@reduxjs/toolkit';
import {
	CustomerDomainEngagementExpectedError,
	FastshiftExpectedError,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { DEFAULT_CONTROLS_PAYLOAD } from '@/common/fastshift-table/src/constants';

import * as StateActions from './actions';
import { FASTSHIFT_LOCALSTORAGE_KEYS, TABLE_IDS } from './constants';
import * as StateTypes from './types';
import { captureErrorWithContext, getRunbookStepActionKey, getUserDomain } from './utils';

export type FastShiftState = {
	userAccess: StateTypes.UserAccess;
	selectedOrg: StateTypes.Org | null;
	organizations: StateTypes.Organizations;
	organizationDetail: StateTypes.OrganizationDetail;
	dcInstanceDetail: StateTypes.DcInstanceDetail;
	customerDomains: StateTypes.FastshiftCustomerDomainsState;
	customerDomain: StateTypes.CustomerDomainDetails;
	customerDomainEngagement: StateTypes.CustomerDomainEngagement;
	topSupportRequests: StateTypes.TopSupportRequests;
	supportRequests: StateTypes.SupportRequests;
	runbooks: StateTypes.Runbooks;
	pageStateCache: StateTypes.PageStateCache;
	// Note only Customer role needs to consent; for other roles, false is expected.
	portalUserConsent: StateTypes.PortalUserConsent;
	csProfiler: StateTypes.CsProfiler;
};

const setRunbookStepActionPending = (state: FastShiftState, runbookId: string, stepId: string) => {
	state.runbooks.stepActionsByStepKey[getRunbookStepActionKey({ runbookId, stepId })] = {
		pending: true,
		error: null,
	};
};

const setRunbookStepActionSettled = (
	state: FastShiftState,
	runbookId: string,
	stepId: string,
	error: string | null,
) => {
	state.runbooks.stepActionsByStepKey[getRunbookStepActionKey({ runbookId, stepId })] = {
		pending: false,
		error,
	};
};

const updateCachedRunbookStep = (
	state: FastShiftState,
	runbookId: string,
	stepId: string,
	stepPatch: Partial<NonNullable<StateTypes.RunbookDetail['data']>['steps'][number]>,
) => {
	const runbook = state.runbooks.detailsByRunbookId[runbookId]?.data;

	if (!runbook) {
		return;
	}

	const step = runbook.steps.find((runbookStep) => {
		return runbookStep.stepId === stepId;
	});

	if (!step) {
		return;
	}

	Object.assign(step, stepPatch);
};

// States here should be post-mapping, meaning that they are not necessarily the same as the response from the API
// but rather the ideal shape of the data that we want to work with. Mapping can be done in the reducer.
export const initialState: FastShiftState = {
	userAccess: {
		pending: false,
		data: null,
		error: null,
	},
	portalUserConsent: {
		pending: false,
		data: null,
		error: null,
	},
	csProfiler: {
		profilingOptions: {
			pending: false,
			data: null,
			error: null,
		},
		customerProfile: {
			pending: false,
			data: null,
			error: null,
			activeRequestId: null,
		},
		linkedTicketsByTheme: {},
	},
	selectedOrg: null,
	customerDomain: {
		pending: false,
		error: null,
		data: null,
	},
	customerDomainEngagement: {
		pending: false,
		error: null,
		data: null,
	},

	// states for partner and fastshift team roles only:
	customerDomains: {
		pending: false,
		error: null,
		data: null,
	},
	topSupportRequests: {
		pending: false,
		error: null,
		data: null,
	},
	supportRequests: {
		pending: false,
		error: null,
		data: null,
	},
	organizations: {
		domainId: null,
		pending: false,
		error: null,
		data: null,
	},
	organizationDetail: {
		domainId: null,
		orgId: null,
		summary: {
			pending: false,
			error: null,
			data: null,
		},
		dcInstances: {
			pending: false,
			error: null,
			data: null,
		},
	},
	dcInstanceDetail: {
		domainId: null,
		orgId: null,
		instanceId: null,
		pending: true,
		error: null,
		data: null,
		recommendationDetailsById: {},
	},
	runbooks: {
		summariesBySupportTicketId: {},
		detailsByRunbookId: {},
		stepActionsByStepKey: {},
	},
	pageStateCache: {
		[TABLE_IDS.SELECTION]: DEFAULT_CONTROLS_PAYLOAD,
		[TABLE_IDS.MIGRATION_TICKETS]: DEFAULT_CONTROLS_PAYLOAD,
	},
};

export const reducer = (state: FastShiftState = initialState) =>
	createReducer(state, (builder) => {
		builder
			.addCase(StateActions.setSelectedOrg, (state, { payload }) => {
				state.selectedOrg = payload;
				// save selected org to localstorage
				localStorage.setItem(
					FASTSHIFT_LOCALSTORAGE_KEYS.SELECTED_ORG_ID,
					JSON.stringify(payload.id),
				);
			})
			.addCase(StateActions.getCustomerDomains.fulfilled, (state, { payload }) => {
				state.customerDomains.pending = false;
				state.customerDomains.data = payload;
				state.customerDomains.error = null;
			})
			.addCase(StateActions.getCustomerDomains.pending, (state) => {
				state.customerDomains.pending = true;
				state.customerDomains.error = null;
			})
			.addCase(StateActions.getCustomerDomains.rejected, (state, action) => {
				state.customerDomains.pending = false;
				state.customerDomains.data = {
					data: [],
					pagination: {
						page: 0,
						size: 20,
						totalElements: 0,
						totalPages: 0,
						hasNext: false,
						hasPrevious: false,
						sorted: false,
					},
				};

				const message = action.error.message ?? 'unknown error';
				state.customerDomains.error = message;

				captureErrorWithContext({
					error: 'Error on fetching customer domains list',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getCustomerDomain.fulfilled, (state, { payload }) => {
				state.customerDomain.pending = false;
				state.customerDomain.data = payload;
				state.customerDomain.error = null;
			})
			.addCase(StateActions.getCustomerDomain.pending, (state) => {
				state.customerDomain.pending = true;
				state.customerDomain.error = null;
			})
			.addCase(StateActions.getCustomerDomain.rejected, (state, action) => {
				state.customerDomain.pending = false;
				state.customerDomain.data = null;
				const message = action.error.message ?? 'unknown error';
				state.customerDomain.error = message;

				captureErrorWithContext({
					error: 'Error on fetching customer domain details',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getCustomerDomainEngagementDetails.fulfilled, (state, { payload }) => {
				state.customerDomainEngagement.pending = false;
				state.customerDomainEngagement.data = payload;
				state.customerDomainEngagement.error = null;
			})
			.addCase(StateActions.getCustomerDomainEngagementDetails.pending, (state) => {
				state.customerDomainEngagement.pending = true;
				state.customerDomainEngagement.error = null;
			})
			.addCase(StateActions.getCustomerDomainEngagementDetails.rejected, (state, action) => {
				state.customerDomainEngagement.pending = false;
				state.customerDomainEngagement.data = null;
				const message = action.error.message ?? 'unknown error';
				state.customerDomainEngagement.error = message;

				// check if error is expected
				const isExpectedError = Object.values(CustomerDomainEngagementExpectedError).includes(
					message as CustomerDomainEngagementExpectedError,
				);

				// if unexpected, capture on Sentry
				if (!isExpectedError) {
					captureErrorWithContext({
						error: 'Error on fetching customer domain engagement details',
						message,
						userDomain: getUserDomain(state),
					});
				}
			})
			// Legal Consent (Only required for Customer role)
			.addCase(StateActions.getPortalUserConsent.pending, (state) => {
				state.portalUserConsent.pending = true;
				state.portalUserConsent.error = null;
				state.portalUserConsent.data = null;
			})
			.addCase(StateActions.getPortalUserConsent.fulfilled, (state, { payload }) => {
				state.portalUserConsent.pending = false;
				state.portalUserConsent.data = payload;
				state.portalUserConsent.error = null;
			})
			.addCase(StateActions.getPortalUserConsent.rejected, (state, action) => {
				state.portalUserConsent.pending = false;
				state.portalUserConsent.data = null;
				const message = action.error.message ?? 'unknown error';
				state.portalUserConsent.error = message;

				// capture on Sentry
				captureErrorWithContext({
					error: 'Error on fetching user consent state',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.submitPortalUserConsent.pending, (state) => {
				state.portalUserConsent.pending = true;
				state.portalUserConsent.error = null;
			})
			.addCase(StateActions.submitPortalUserConsent.fulfilled, (state, { payload }) => {
				state.portalUserConsent.pending = false;
				state.portalUserConsent.data = payload;
				state.portalUserConsent.error = null;
			})
			.addCase(StateActions.submitPortalUserConsent.rejected, (state, action) => {
				state.portalUserConsent.pending = false;
				const message = action.error.message ?? 'unknown error';
				state.portalUserConsent.error = message;

				// capture on Sentry
				captureErrorWithContext({
					error: 'Error on submitting user consent state',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.setPortalUserConsent, (state, { payload }) => {
				state.portalUserConsent.data = payload;
			})
			.addCase(StateActions.getFastshiftOrganizations.pending, (state, { meta }) => {
				const isSameDomain = state.organizations.domainId === meta.arg.domainId;

				state.organizations = {
					domainId: meta.arg.domainId,
					pending: true,
					error: null,
					data: isSameDomain ? state.organizations.data : null,
				};
			})
			.addCase(StateActions.getFastshiftOrganizations.fulfilled, (state, { payload, meta }) => {
				if (state.organizations.domainId !== meta.arg.domainId) {
					return;
				}

				state.organizations.pending = false;
				state.organizations.error = null;
				state.organizations.data = payload.data;
			})
			.addCase(StateActions.getFastshiftOrganizations.rejected, (state, action) => {
				if (state.organizations.domainId !== action.meta.arg.domainId) {
					return;
				}

				state.organizations.pending = false;
				const message = action.error.message ?? 'unknown error';
				state.organizations.error = message;

				captureErrorWithContext({
					error: 'Error on fetching organizations',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftOrganizationSummary.pending, (state, { meta }) => {
				const { domainId, orgId } = meta.arg;
				const isSameOrganization =
					state.organizationDetail.domainId === domainId &&
					state.organizationDetail.orgId === orgId;

				state.organizationDetail.domainId = domainId;
				state.organizationDetail.orgId = orgId;
				state.organizationDetail.summary = {
					pending: true,
					error: null,
					data: isSameOrganization ? state.organizationDetail.summary.data : null,
				};

				if (!isSameOrganization) {
					state.organizationDetail.dcInstances = initialState.organizationDetail.dcInstances;
				}
			})
			.addCase(
				StateActions.getFastshiftOrganizationSummary.fulfilled,
				(state, { payload, meta }) => {
					const { domainId, orgId } = meta.arg;

					if (
						state.organizationDetail.domainId !== domainId ||
						state.organizationDetail.orgId !== orgId
					) {
						return;
					}

					state.organizationDetail.summary = {
						pending: false,
						error: null,
						data: payload.data,
					};
				},
			)
			.addCase(StateActions.getFastshiftOrganizationSummary.rejected, (state, action) => {
				const { domainId, orgId } = action.meta.arg;

				if (
					state.organizationDetail.domainId !== domainId ||
					state.organizationDetail.orgId !== orgId
				) {
					return;
				}

				const message = action.error.message ?? 'unknown error';
				state.organizationDetail.summary = {
					pending: false,
					data: state.organizationDetail.summary.data,
					error: message,
				};

				if (message !== FastshiftExpectedError.NOT_FOUND) {
					captureErrorWithContext({
						error: 'Error on fetching organization summary',
						message,
						userDomain: getUserDomain(state),
					});
				}
			})
			.addCase(StateActions.getFastshiftOrganizationDcInstances.pending, (state, { meta }) => {
				const isSameOrganization =
					state.organizationDetail.domainId === meta.arg.domainId &&
					state.organizationDetail.orgId === meta.arg.orgId;

				state.organizationDetail.domainId = meta.arg.domainId;
				state.organizationDetail.orgId = meta.arg.orgId;
				if (!isSameOrganization) {
					state.organizationDetail.summary = initialState.organizationDetail.summary;
				}
				state.organizationDetail.dcInstances = {
					pending: true,
					error: null,
					data: isSameOrganization ? state.organizationDetail.dcInstances.data : null,
				};
			})
			.addCase(
				StateActions.getFastshiftOrganizationDcInstances.fulfilled,
				(state, { payload, meta }) => {
					if (
						state.organizationDetail.domainId !== meta.arg.domainId ||
						state.organizationDetail.orgId !== meta.arg.orgId
					) {
						return;
					}

					const { dcInstances } = state.organizationDetail;
					dcInstances.pending = false;
					dcInstances.error = null;
					dcInstances.data = payload.data;
				},
			)
			.addCase(StateActions.getFastshiftOrganizationDcInstances.rejected, (state, action) => {
				if (
					state.organizationDetail.domainId !== action.meta.arg.domainId ||
					state.organizationDetail.orgId !== action.meta.arg.orgId
				) {
					return;
				}

				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				const { dcInstances } = state.organizationDetail;
				dcInstances.pending = false;
				const message = action.error.message ?? 'unknown error';
				dcInstances.error = message;

				if (message !== FastshiftExpectedError.NOT_FOUND) {
					captureErrorWithContext({
						error: 'Error on fetching organization DC instances',
						message,
						userDomain: getUserDomain(state),
					});
				}
			})
			.addCase(StateActions.getFastshiftDcInstanceDetails.pending, (state, { meta }) => {
				state.dcInstanceDetail = {
					domainId: meta.arg.domainId,
					orgId: meta.arg.orgId,
					instanceId: meta.arg.instanceId,
					pending: true,
					error: null,
					data: null,
					recommendationDetailsById: {},
				};
			})
			.addCase(StateActions.getFastshiftDcInstanceDetails.fulfilled, (state, { payload, meta }) => {
				const { domainId, orgId, instanceId } = meta.arg;
				if (
					state.dcInstanceDetail.domainId !== domainId ||
					state.dcInstanceDetail.orgId !== orgId ||
					state.dcInstanceDetail.instanceId !== instanceId
				) {
					return;
				}

				state.dcInstanceDetail.pending = false;
				state.dcInstanceDetail.error = null;
				state.dcInstanceDetail.data = payload.data;
			})
			.addCase(StateActions.getFastshiftDcInstanceDetails.rejected, (state, action) => {
				const { domainId, orgId, instanceId } = action.meta.arg;
				if (
					state.dcInstanceDetail.domainId !== domainId ||
					state.dcInstanceDetail.orgId !== orgId ||
					state.dcInstanceDetail.instanceId !== instanceId
				) {
					return;
				}

				const message = action.error.message ?? 'unknown error';
				state.dcInstanceDetail.pending = false;
				state.dcInstanceDetail.error = message;
				state.dcInstanceDetail.data = null;

				if (message !== FastshiftExpectedError.NOT_FOUND) {
					captureErrorWithContext({
						error: 'Error on fetching DC instance details',
						message,
						userDomain: getUserDomain(state),
					});
				}
			})
			.addCase(
				StateActions.getFastshiftAssessmentRecommendationDetails.pending,
				(state, { meta }) => {
					const { domainId, orgId, instanceId, assessmentId, recommendationId } = meta.arg;
					if (
						state.dcInstanceDetail.domainId !== domainId ||
						state.dcInstanceDetail.orgId !== orgId ||
						state.dcInstanceDetail.instanceId !== instanceId ||
						state.dcInstanceDetail.data?.assessment.assessmentId !== assessmentId
					) {
						return;
					}

					const cachedDetail = state.dcInstanceDetail.recommendationDetailsById[recommendationId];
					state.dcInstanceDetail.recommendationDetailsById[recommendationId] = {
						pending: true,
						error: null,
						data: cachedDetail?.data ?? null,
					};
				},
			)
			.addCase(
				StateActions.getFastshiftAssessmentRecommendationDetails.fulfilled,
				(state, { payload, meta }) => {
					const { domainId, orgId, instanceId, assessmentId, recommendationId } = meta.arg;
					if (
						state.dcInstanceDetail.domainId !== domainId ||
						state.dcInstanceDetail.orgId !== orgId ||
						state.dcInstanceDetail.instanceId !== instanceId ||
						state.dcInstanceDetail.data?.assessment.assessmentId !== assessmentId
					) {
						return;
					}

					state.dcInstanceDetail.recommendationDetailsById[recommendationId] = {
						pending: false,
						error: null,
						data: payload.data,
					};
				},
			)
			.addCase(
				StateActions.getFastshiftAssessmentRecommendationDetails.rejected,
				(state, action) => {
					const { domainId, orgId, instanceId, assessmentId, recommendationId } = action.meta.arg;
					if (
						state.dcInstanceDetail.domainId !== domainId ||
						state.dcInstanceDetail.orgId !== orgId ||
						state.dcInstanceDetail.instanceId !== instanceId ||
						state.dcInstanceDetail.data?.assessment.assessmentId !== assessmentId
					) {
						return;
					}

					const cachedDetail = state.dcInstanceDetail.recommendationDetailsById[recommendationId];
					const message = action.error.message ?? 'unknown error';

					state.dcInstanceDetail.recommendationDetailsById[recommendationId] = {
						pending: false,
						error: message,
						data: cachedDetail?.data ?? null,
					};

					if (message !== FastshiftExpectedError.NOT_FOUND) {
						captureErrorWithContext({
							error: 'Error on fetching assessment recommendation details',
							message,
							userDomain: getUserDomain(state),
						});
					}
				},
			)
			// Fastshift support requests (M2-specific)
			.addCase(StateActions.getFastshiftSupportRequests.pending, (state) => {
				state.supportRequests.pending = true;
				state.supportRequests.error = null;
				state.supportRequests.data = null;
			})
			.addCase(StateActions.getFastshiftSupportRequests.fulfilled, (state, { payload, meta }) => {
				state.supportRequests.pending = false;
				state.supportRequests.data = payload.data;
				state.supportRequests.error = null;
				state.supportRequests.lastFetchParams = {
					customerDomainId: meta.arg.customerDomainId,
					queryParams: meta.arg.queryParams,
				};
			})
			.addCase(StateActions.getFastshiftSupportRequests.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				state.supportRequests.pending = false;
				state.supportRequests.data = null;
				const message = action.error.message ?? 'unknown error';

				state.supportRequests.error = message;

				captureErrorWithContext({
					error: 'Error on fetching support requests',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftTopSupportRequests.pending, (state) => {
				state.topSupportRequests.pending = true;
				state.topSupportRequests.error = null;
				state.topSupportRequests.data = null;
			})
			.addCase(
				StateActions.getFastshiftTopSupportRequests.fulfilled,
				(state, { payload, meta }) => {
					state.topSupportRequests.pending = false;
					state.topSupportRequests.data = payload;
					state.topSupportRequests.error = null;
					state.topSupportRequests.lastFetchParams = {
						customerDomainId: meta.arg.domainId,
						queryParams: meta.arg.queryParams,
					};
				},
			)
			.addCase(StateActions.getFastshiftTopSupportRequests.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				state.topSupportRequests.pending = false;
				state.topSupportRequests.data = null;
				const message = action.error.message ?? 'unknown error';
				state.topSupportRequests.error = message;

				captureErrorWithContext({
					error: 'Error on fetching top support requests',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.clearAllCustomerDomainScopedState, (state) => {
				state.customerDomain.pending = false;
				state.customerDomain.error = null;
				state.customerDomain.data = null;
				state.customerDomainEngagement.pending = false;
				state.customerDomainEngagement.error = null;
				state.customerDomainEngagement.data = null;
				state.pageStateCache[TABLE_IDS.MIGRATION_TICKETS] =
					initialState.pageStateCache[TABLE_IDS.MIGRATION_TICKETS];
				state.supportRequests.pending = false;
				state.supportRequests.error = null;
				state.supportRequests.data = null;
				state.supportRequests.lastFetchParams = undefined;
				state.topSupportRequests.pending = false;
				state.topSupportRequests.error = null;
				state.topSupportRequests.data = null;
				state.topSupportRequests.lastFetchParams = undefined;
				state.organizations = initialState.organizations;
				state.organizationDetail = initialState.organizationDetail;
				state.dcInstanceDetail = initialState.dcInstanceDetail;
				state.runbooks = initialState.runbooks;
			})
			.addCase(StateActions.clearCustomerDomain, (state) => {
				state.customerDomain.pending = false;
				state.customerDomain.data = null;
				state.customerDomain.error = null;
			})
			.addCase(StateActions.setTableControls, (state, { payload }) => {
				state.pageStateCache[payload.tableId] = payload.controls;
			})
			.addCase(StateActions.clearTableControls, (state, { payload }) => {
				state.pageStateCache[payload.tableId] = initialState.pageStateCache[payload.tableId];
			})
			.addCase(StateActions.getUserAccess.fulfilled, (state, { payload }) => {
				state.userAccess.pending = false;
				state.userAccess.data = payload;
				state.userAccess.error = null;
			})
			.addCase(StateActions.getUserAccess.pending, (state) => {
				state.userAccess.pending = true;
				state.userAccess.error = null;
			})
			.addCase(StateActions.getUserAccess.rejected, (state, action) => {
				state.userAccess.pending = false;
				state.userAccess.data = null;
				const message = action.error.message ?? 'unknown error';
				state.userAccess.error = message;

				captureErrorWithContext({
					error: 'Error on fetching user access',
					message,
				});
			})
			.addCase(StateActions.getFastshiftRunbooksBySupportTicketId.pending, (state, { meta }) => {
				const { supportTicketId } = meta.arg;
				state.runbooks.summariesBySupportTicketId[supportTicketId] = {
					pending: true,
					error: null,
					data: state.runbooks.summariesBySupportTicketId[supportTicketId]?.data ?? null,
				};
			})
			.addCase(
				StateActions.getFastshiftRunbooksBySupportTicketId.fulfilled,
				(state, { payload, meta }) => {
					state.runbooks.summariesBySupportTicketId[meta.arg.supportTicketId] = {
						pending: false,
						error: null,
						data: payload,
					};
				},
			)
			.addCase(StateActions.getFastshiftRunbooksBySupportTicketId.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				const { supportTicketId } = action.meta.arg;
				state.runbooks.summariesBySupportTicketId[supportTicketId] = {
					pending: false,
					data: state.runbooks.summariesBySupportTicketId[supportTicketId]?.data ?? null,
					error: action.error.message ?? 'unknown error',
				};

				captureErrorWithContext({
					error: 'Error on fetching runbooks by support ticket id',
					message: action.error.message ?? 'unknown error',
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftRunbook.pending, (state, { meta }) => {
				const { runbookId } = meta.arg;
				state.runbooks.detailsByRunbookId[runbookId] = {
					pending: true,
					error: null,
					data: state.runbooks.detailsByRunbookId[runbookId]?.data ?? null,
				};
			})
			.addCase(StateActions.getFastshiftRunbook.fulfilled, (state, { payload, meta }) => {
				state.runbooks.detailsByRunbookId[meta.arg.runbookId] = {
					pending: false,
					error: null,
					data: payload,
				};
			})
			.addCase(StateActions.getFastshiftRunbook.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				const { runbookId } = action.meta.arg;
				state.runbooks.detailsByRunbookId[runbookId] = {
					pending: false,
					data: state.runbooks.detailsByRunbookId[runbookId]?.data ?? null,
					error: action.error.message ?? 'unknown error',
				};

				captureErrorWithContext({
					error: 'Error on fetching runbook',
					message: action.error.message ?? 'unknown error',
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepStatus.pending, (state, { meta }) => {
				setRunbookStepActionPending(state, meta.arg.runbookId, meta.arg.stepId);
			})
			.addCase(StateActions.updateFastshiftRunbookStepStatus.fulfilled, (state, { meta }) => {
				const { runbookId, stepId } = meta.arg;

				setRunbookStepActionSettled(state, runbookId, stepId, null);
				updateCachedRunbookStep(state, runbookId, stepId, {
					status: meta.arg.status,
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepStatus.rejected, (state, action) => {
				const { runbookId, stepId } = action.meta.arg;
				const message = action.error.message ?? 'unknown error';

				setRunbookStepActionSettled(state, runbookId, stepId, message);

				captureErrorWithContext({
					error: 'Error on updating runbook step status',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepRisk.pending, (state, { meta }) => {
				setRunbookStepActionPending(state, meta.arg.runbookId, meta.arg.stepId);
			})
			.addCase(StateActions.updateFastshiftRunbookStepRisk.fulfilled, (state, { meta }) => {
				const { runbookId, stepId } = meta.arg;

				setRunbookStepActionSettled(state, runbookId, stepId, null);
				updateCachedRunbookStep(state, runbookId, stepId, {
					risk: meta.arg.risk,
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepRisk.rejected, (state, action) => {
				const { runbookId, stepId } = action.meta.arg;
				const message = action.error.message ?? 'unknown error';

				setRunbookStepActionSettled(state, runbookId, stepId, message);

				captureErrorWithContext({
					error: 'Error on updating runbook step risk',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepDisabled.pending, (state, { meta }) => {
				setRunbookStepActionPending(state, meta.arg.runbookId, meta.arg.stepId);
			})
			.addCase(StateActions.updateFastshiftRunbookStepDisabled.fulfilled, (state, { meta }) => {
				const { runbookId, stepId } = meta.arg;

				setRunbookStepActionSettled(state, runbookId, stepId, null);
				updateCachedRunbookStep(state, runbookId, stepId, {
					disabled: meta.arg.disabled,
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepDisabled.rejected, (state, action) => {
				const { runbookId, stepId } = action.meta.arg;
				const message = action.error.message ?? 'unknown error';

				setRunbookStepActionSettled(state, runbookId, stepId, message);

				captureErrorWithContext({
					error: 'Error on updating runbook step disabled state',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftCsProfilerProfilingOptions.pending, (state) => {
				state.csProfiler.profilingOptions.pending = true;
				state.csProfiler.profilingOptions.error = null;
			})
			.addCase(
				StateActions.getFastshiftCsProfilerProfilingOptions.fulfilled,
				(state, { payload }) => {
					state.csProfiler.profilingOptions.pending = false;
					state.csProfiler.profilingOptions.data = payload;
					state.csProfiler.profilingOptions.error = null;
				},
			)
			.addCase(StateActions.getFastshiftCsProfilerProfilingOptions.rejected, (state, action) => {
				const message = action.error.message ?? 'unknown error';

				state.csProfiler.profilingOptions.pending = false;
				state.csProfiler.profilingOptions.data = null;
				state.csProfiler.profilingOptions.error = message;

				captureErrorWithContext({
					error: 'Error on fetching CS profiler profiling options',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftCsProfilerCustomerProfile.pending, (state, { meta }) => {
				state.csProfiler.customerProfile.pending = true;
				state.csProfiler.customerProfile.data = null;
				state.csProfiler.customerProfile.error = null;
				state.csProfiler.customerProfile.activeRequestId = meta.requestId;
				state.csProfiler.linkedTicketsByTheme = {};
			})
			.addCase(
				StateActions.getFastshiftCsProfilerCustomerProfile.fulfilled,
				(state, { payload, meta }) => {
					if (state.csProfiler.customerProfile.activeRequestId !== meta.requestId) {
						return;
					}

					state.csProfiler.customerProfile.pending = false;
					state.csProfiler.customerProfile.data = payload;
					state.csProfiler.customerProfile.error = null;
					state.csProfiler.customerProfile.activeRequestId = null;
				},
			)
			.addCase(StateActions.getFastshiftCsProfilerCustomerProfile.rejected, (state, action) => {
				if (state.csProfiler.customerProfile.activeRequestId !== action.meta.requestId) {
					return;
				}

				const message = action.error.message ?? 'unknown error';

				state.csProfiler.customerProfile.pending = false;
				state.csProfiler.customerProfile.data = null;
				state.csProfiler.customerProfile.error = message;
				state.csProfiler.customerProfile.activeRequestId = null;

				captureErrorWithContext({
					error: 'Error on fetching CS profiler customer profile',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftCsProfilerLinkedTickets.pending, (state, { meta }) => {
				const existingState = state.csProfiler.linkedTicketsByTheme[meta.arg.theme];

				state.csProfiler.linkedTicketsByTheme[meta.arg.theme] = {
					pending: true,
					data: existingState?.data ?? null,
					error: null,
				};
			})
			.addCase(
				StateActions.getFastshiftCsProfilerLinkedTickets.fulfilled,
				(state, { payload, meta }) => {
					state.csProfiler.linkedTicketsByTheme[meta.arg.theme] = {
						pending: false,
						data: payload,
						error: null,
					};
				},
			)
			.addCase(StateActions.getFastshiftCsProfilerLinkedTickets.rejected, (state, action) => {
				const message = action.error.message ?? 'unknown error';

				state.csProfiler.linkedTicketsByTheme[action.meta.arg.theme] = {
					pending: false,
					data: state.csProfiler.linkedTicketsByTheme[action.meta.arg.theme]?.data ?? null,
					error: message,
				};

				captureErrorWithContext({
					error: 'Error on fetching CS profiler linked tickets',
					message,
					userDomain: getUserDomain(state),
				});
			});
	});
